import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useHistory, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import ContentHeader from '../Layout/ContentHeader'
import { toastSweetAlert } from '../Helpers/ToastSweetAlert'
import Box from '../Global/Box'
import InputController from '../Global/InputController'
import { useLoading } from '../../LoadingOverlay/LoadingOverlayContext'
import {
  CREATE_CONFIG_APP,
  GET_ALL_CONFIG_APP,
  GET_ONE_CONFIG_APP,
  UPDATE_CONFIG_APP,
  UPDATE_CONFIG_APP_ONLY_FILE,
  UPDATE_CONFIG_APP_ONLY_FILE_DELETE,
} from '../../graphql/ConfigApp/configApp'

const extensions_allowed_image = ['.png', '.jpg', '.jpeg']
const sizeMaxFile = 8 // EXPRESADO EN MB.
const refetchConfigsApp = {
  query: GET_ALL_CONFIG_APP,
  variables: {
    limit: 10,
    offset: 0,
    searchQuery: null,
  },
}

const backPage = '/ConfigApp/Configs'

const optionsVisible = [
  {
    value: true,
    label: 'Si',
  },
  {
    value: false,
    label: 'No',
  },
]
const languages = [
  {
    code: 'es',
    name: 'spanish-mx',
    text: 'Español México',
    icon: '',
  },
  {
    code: 'en',
    name: 'english-us',
    text: 'English US',
    icon: '',
  },
]

export const ConfigAppNew = () => {
  const { id: _id } = useParams()
  const history = useHistory()
  const { showLoading, hideLoading } = useLoading()

  //STATES:
  const [data, setData] = useState(null)
  const [file, setFile] = useState(null)

  //MUTATIONS:
  const [createConfigApp] = useMutation(CREATE_CONFIG_APP, {
    refetchQueries: [{ ...refetchConfigsApp }],
  })

  const [updateOnlyFile] = useMutation(UPDATE_CONFIG_APP_ONLY_FILE, {
    refetchQueries: [
      { ...refetchConfigsApp },
      { query: GET_ONE_CONFIG_APP, variables: { id: parseInt(_id) } },
    ],
  })

  const [updateOnlyFileDelete] = useMutation(
    UPDATE_CONFIG_APP_ONLY_FILE_DELETE,
    {
      refetchQueries: [
        { ...refetchConfigsApp },
        { query: GET_ONE_CONFIG_APP, variables: { id: parseInt(_id) } },
      ],
    }
  )

  const [updateConfigApp] = useMutation(UPDATE_CONFIG_APP, {
    refetchQueries: [
      { ...refetchConfigsApp },
      { query: GET_ONE_CONFIG_APP, variables: { id: parseInt(_id) } },
    ],
  })

  //QUERIES:
  const {
    data: dataResp,
    loading,
    error,
  } = useQuery(GET_ONE_CONFIG_APP, {
    variables: {
      id: parseInt(_id),
    },
  })

  //FORM:
  const validationSchema = Yup.object().shape({
    visible: Yup.string().required('Campo requerido'),
    app_name: Yup.string().required('Campo requerido'),
    version: Yup.string().required('Campo requerido'),
    theme_source: Yup.string().required('Campo requerido'),
    theme_font_name: Yup.string().required('Campo requerido'),
    theme_font_size: Yup.string().required('Campo requerido'),
  })

  const {
    methods,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  //EFFECTS:
  useEffect(() => {
    if (!loading) {
      if (_id) {
        if (error) {
          return toastSweetAlert({
            mode: 'error',
            message: error.message,
          })
        }

        if (dataResp !== undefined && dataResp !== null) {
          const data = dataResp.getOneConfigApp

          const json_config = JSON.parse(data.json_config)

          setData({ ...data, json_config: json_config })

          const dataFormated = {
            visible: data.visible,
            app_name: json_config.app.app_name ?? '',
            version: json_config.app.version ?? '',
            theme_source: json_config.theme.source ?? '',
            theme_font_name: json_config.theme.font.name ?? '',
            theme_font_size: json_config.theme.font.size ?? '',
          }

          reset(dataFormated)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_id, dataResp, error, loading])

  const handleFile = async (e) => {
    if (!e.target.files[0]) return

    const fileName = e.target.files[0].name.toLowerCase()
    const lastDot = fileName.lastIndexOf('.')
    const extension = fileName.slice(lastDot, fileName.length)

    if (extensions_allowed_image.indexOf(extension) === -1) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: 'Solo se admite JPG, JPGE y PNG',
      })
    }

    if (e.target.files[0].size / 1048576 > sizeMaxFile) {
      setFile(null)
      return toastSweetAlert({
        mode: 'error',
        message: `El archivo no puede superar los ${sizeMaxFile}MB`,
      })
    }

    setFile(e.target.files[0])
  }

  const handleSubmitUpdateOnlyFile = async () => {
    if (!file) {
      return toastSweetAlert({
        mode: 'errorToast',
        message: 'Se requiere elegir imagen',
      })
    }

    const input = { file, urlKey: data.json_config.app.app_logo }
    try {
      await updateOnlyFile({
        variables: { configAppId: parseInt(_id), input },
      })
      return toastSweetAlert({ mode: 'ok', message: 'Se actualizo la imagen' })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  const handleSubmitUpdateOnlyFileDelete = async () => {
    try {
      await updateOnlyFileDelete({
        variables: {
          configAppId: parseInt(_id),
          urlKey: data.json_config.app.app_logo,
        },
      })
      return toastSweetAlert({ mode: 'ok', message: 'Se elimino la imagen' })
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }

  const onSubmit = async (dataForm) => {
    const app = {
      app_name: dataForm.app_name,
      file: null,
      version: dataForm.version,
    }

    const theme = {
      source: dataForm.theme_source,
      font: {
        name: dataForm.theme_font_name,
        size: parseInt(dataForm.theme_font_size),
      },
    }

    const input = {
      app: app,
      theme: JSON.stringify(theme),
      languages: JSON.stringify(languages),
      visible: dataForm.visible === 'true' ? true : false,
    }

    showLoading()

    try {
      let message = ''

      if (_id) {
        await updateConfigApp({
          variables: { configAppId: parseInt(_id), input },
        })
        message = 'Se actualizo correctamente'
      } else {
        if (file) input.app.file = file
        await createConfigApp({ variables: { input } })
        message = 'Se creo con exito'
      }

      return toastSweetAlert({ mode: 'ok', message }, history.push(backPage))
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    } finally {
      hideLoading()
    }
  }

  return (
    <>
      <ContentHeader
        title=""
        breadcrumb="custom"
        titleContent={
          <div className="col-12 col-sm-6">
            <Link className="btn btn-migrates-white" to={backPage}>
              <i className="fas fa-long-arrow-alt-left" /> Atrás
            </Link>
          </div>
        }
        breadcrumbContent={
          <div className="col-12 col-sm-6">
            <ol className="breadcrumb float-sm-right">
              <li className="breadcrumb-item">
                <Link to={backPage}>Config. App / </Link>
              </li>
              <li className="breadcrumb-iteem active">
                {` `}
                {_id ? 'Editar' : 'Agregar'}
              </li>
            </ol>
          </div>
        }
        windowTitle={`${_id ? 'Editar' : 'Agregar'} configuracion App`}
      />
      <FormProvider {...methods}>
        <form className="px-3" onSubmit={handleSubmit(onSubmit)}>
          <Box
            title="Configuracion App"
            btnRedPath={backPage}
            btnRedTxt="Cancelar"
            errors={errors}
            btnSubmit={true}
            content={
              <div className="mb-3">
                <div className="row">
                  <div className="mb-3 col-12 col-sm-3 col-md-2">
                    <InputController
                      label="Visible"
                      name="visible"
                      control={control}
                      inputType="choosen"
                      options={optionsVisible}
                      placeholderTxt="Elige uno"
                      isClearable={true}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-3 col-md-3">
                    <InputController
                      label="Nombre App"
                      name="app_name"
                      type="text"
                      placeholderTxt="Escribe nombre"
                      control={control}
                    />
                  </div>
                  <div className="mb-3 col-12 col-sm-3 col-md-2">
                    <InputController
                      label="Version App"
                      name="version"
                      type="text"
                      placeholderTxt="Escribe version"
                      control={control}
                    />
                  </div>
                  {!_id && (
                    <div className="mb-3 col-12 col-sm-4 col-md-5">
                      <InputController
                        label="Logo App"
                        inputType="file"
                        formatAccept="image/*"
                        validateFormat="imageavatar"
                        validateFormatMsg="Solo se admite JPG, JPGE y PNG"
                        name="file"
                        control={control}
                        changeAction={(e) => handleFile(e)}
                      />
                    </div>
                  )}
                </div>
                <div className="pr-5 mt-3 d-flex">
                  <Box
                    title="Tema"
                    content={
                      <div
                        className="d-flex flex-wrap"
                        style={{ gap: '15px 15px' }}
                      >
                        <div className="">
                          <InputController
                            label="Color fuente"
                            name="theme_source"
                            type="text"
                            placeholderTxt="Ejem. #FFFFFF"
                            control={control}
                          />
                        </div>
                        <div className="">
                          <InputController
                            label="Font_name"
                            name="theme_font_name"
                            type="text"
                            placeholderTxt="Escribe fuente"
                            control={control}
                          />
                        </div>
                        <div className="">
                          <InputController
                            label="Font_size"
                            name="theme_font_size"
                            inputType="number"
                            placeholderTxt="Numero"
                            control={control}
                          />
                        </div>
                      </div>
                    }
                  />
                </div>
              </div>
            }
          />
        </form>
      </FormProvider>

      {_id && (
        <div className="mt-2 p-3">
          <Box
            title="Subir o actualizar logo"
            btnRedPath={backPage}
            btnRedTxt="Cancelar"
            btnSubmitNoEnter={() => handleSubmitUpdateOnlyFile()}
            btnSubmitNoEnterTxtSave={`${
              data?.json_config?.app?.app_logo_url &&
              data?.json_config?.app?.app_logo
                ? 'Actualizar logo'
                : 'Subir logo'
            }`}
            btnSubmitNoEnterTxtUpdate={`${
              data?.json_config?.app?.app_logo_url &&
              data?.json_config?.app?.app_logo
                ? 'Actualizar logo'
                : 'Subir logo'
            }`}
            content={
              <div className="row mb-5">
                <div className="mb-3 col-12 col-sm-6">
                  <InputController
                    label="Imagen"
                    inputType="file"
                    formatAccept="image/*"
                    validateFormat="imageavatar"
                    validateFormatMsg="Solo se admite JPG, JPGE y PNG"
                    name="file"
                    control={control}
                    changeAction={(e) => handleFile(e)}
                  />
                </div>
                {data?.json_config?.app?.app_logo_url &&
                  data?.json_config?.app?.app_logo && (
                    <div className="col-12 col-sm-6">
                      <label>Logo actual</label>
                      <div>
                        <img
                          src={data?.json_config?.app?.app_logo_url}
                          width={'100%'}
                          alt="Logo"
                        />
                      </div>
                      <button
                        className="btn btn-danger mt-3 float-right"
                        onClick={() => handleSubmitUpdateOnlyFileDelete()}
                      >
                        <i className="fas fa-trash-alt" /> Eliminar logo
                      </button>
                    </div>
                  )}
              </div>
            }
          />
        </div>
      )}
    </>
  )
}
export default ConfigAppNew
